// Draw clouds on the dedicated clouds canvas
// Each circle uses a radial gradient (white center → transparent edge)
// so overlapping circles blend into soft, organic cloud shapes.

export class CloudRenderer {
  constructor() {
    // Cache gradients keyed by rounded radius to avoid creating new ones each frame
    this._gradientCache = new Map();
    this._cacheCtx = null;
  }

  _getGradient(ctx, cx, cy, r) {
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.4, 'rgba(255,255,255,0.8)');
    grad.addColorStop(0.7, 'rgba(255,255,255,0.3)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    return grad;
  }

  /**
   * @param {CanvasRenderingContext2D} ctx - clouds canvas context
   * @param {import('./CloudSystem.mjs').CloudSystem} cloudSystem
   * @param {object} camera - { x, y, zoom }
   * @param {number} canvasW
   * @param {number} canvasH
   * @param {number} nowMs
   * @param {object} [weatherState] - optional WeatherState for alpha modulation
   */
  render(ctx, cloudSystem, camera, canvasW, canvasH, nowMs, weatherState) {
    ctx.clearRect(0, 0, canvasW, canvasH);

    const timeSec = nowMs / 1000;
    // Alpha multiplier: 0.8x (clear) to 1.5x (full cover)
    const alphaMul = weatherState ? 0.8 + weatherState.cloudCover01 * 0.7 : 1;

    for (let li = 0; li < cloudSystem.layerCount; li++) {
      const visible = cloudSystem.getVisibleClouds(li, camera, canvasW, canvasH);

      for (const { cloud, sx, sy, scale } of visible) {
        // Wobble offset
        const wobbleX = Math.sin(timeSec * cloud.wobbleSpeed + cloud.wobblePhase) * cloud.wobbleAmp * scale;
        const wobbleY = Math.cos(timeSec * cloud.wobbleSpeed * 0.7 + cloud.wobblePhase) * cloud.wobbleAmp * 0.5 * scale;

        ctx.globalAlpha = cloud.alpha * alphaMul;

        for (const circle of cloud.circles) {
          const cx = sx + circle.ox * scale + wobbleX;
          const cy = sy + circle.oy * scale + wobbleY;
          const r = circle.r * scale;

          ctx.fillStyle = this._getGradient(ctx, cx, cy, r);
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    ctx.globalAlpha = 1;
  }
}
